import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  X,
  Mail,
  Send,
  Check,
  AlertTriangle,
  Link,
  Upload,
  Trash2,
  Eye,
  FileText,
  File as FileIcon,
  MessageSquare,
  ShieldCheck,
  Lock,
  Plus,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  sendCmrAttachments,
  fetchShipmentXMLFile,
} from "../../store/slices/shipmentSlice";
import Button from "../UI/Button";
import { createCmrPdf } from "../../utils/pdfGenerator"; //Generate PDF Bytes
import { createDeliveryNotePdf } from "../../utils/generateDeliveryNote";
import { createBillOfLadingPdf } from "../../utils/generateBillOfLading";
import { createAirWaybillPdf } from "../../utils/generateAirWaybill";
import { createShipmentOrderPdf } from "../../utils/generateShipmentOrder";
import { isShipmentDocumentReady } from "../../utils/shipmentDocumentStatus";
import { Shipment } from "../../types";
import { toast } from "react-toastify";
import { sendDeliveryOtp } from "../../store/slices/shipmentSlice";
import { shipmentAccessAPI } from "../../services/api";
import { isValidPhoneFormat } from "../../utils/phone";

interface AccessRequest {
  _id: string;
  email: string;
  status: "pending" | "accepted" | "rejected" | "revoked";
  createdAt: string;
}

interface CMRSharingModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: Shipment | null;
}

interface AttachedFile {
  id: string;
  name: string;
  type: "cmr-pdf" | "xml" | "upload";
  size: number;
  url?: string;
  file?: File;
  canDelete?: boolean;
}

const CMRSharingModal: React.FC<CMRSharingModalProps> = ({
  isOpen,
  onClose,
  documentId,
}) => {
  const { t } = useTranslation("shipments");

  // Tab state
  const [activeTab, setActiveTab] = useState<
    "email" | "driver" | "shipment" | "otp" | "so"
  >("email");

  // SO Document tab — email the Shipment Order with the SO PDF auto-attached.
  const [soEmail, setSoEmail] = useState({
    recipients: "",
    subject: "",
    message: "",
  });
  const [soFile, setSoFile] = useState<File | null>(null);
  const [soGenerating, setSoGenerating] = useState(false);

  // Email form data
  const [emailData, setEmailData] = useState({
    recipients: "",
    subject: t("sharing.subjectDefault", { id: documentId?._id }),
    message: "",
    includePDF: true,
  });

  const dispatch = useAppDispatch();
  const { sendEmailLoading: loading, otpLoading } = useAppSelector(
    (state) => state.shipment,
  );
  const { countries } = useAppSelector((state) => state.company);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isDriverLinkCopied, setIsDriverLinkCopied] = useState(false);
  const [isXmlLoading, setIsXmlLoading] = useState(false);
  const [whatsappPhoneNumber, setWhatsappPhoneNumber] = useState("");

  const [otpEmail, setOtpEmail] = useState("");

  // Allowed emails (shipment link access control)
  const [emailTags, setEmailTags] = useState<{ email: string; role: "view" | "poh_handler" }[]>([]);
  const [emailTagInput, setEmailTagInput] = useState("");
  const [emailTagRole, setEmailTagRole] = useState<"view" | "poh_handler">("view");
  const [savingEmails, setSavingEmails] = useState(false);

  const autoSaveEmails = useCallback(
    async (tags: { email: string; role: "view" | "poh_handler" }[]) => {
      if (!documentId) return;
      setSavingEmails(true);
      try {
        await shipmentAccessAPI.updateAllowedEmails(documentId._id, tags);
      } catch {
        toast.error("Failed to update access settings");
      } finally {
        setSavingEmails(false);
      }
    },
    [documentId],
  );

  // Access requests
  const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const isInternational = documentId?.type === "International";
  // The Drivers App tab is a ROAD-only mechanism (driver app + public link).
  // Sea/air shipments have no driver, so the tab is hidden for them.
  const transportMode = documentId?.transportMode || "ROAD";
  const isRoad = transportMode === "ROAD";
  const isSeaDoc = transportMode === "SEA";
  const isAirDoc = transportMode === "AIR";

  // Document label per transport mode: B/L (SEA), AWB (AIR), or the road docs
  // (CMR for International, Delivery Note for Domestic). Drives file naming +
  // which document the backend XML endpoint returns.
  const xmlFileName = isSeaDoc
    ? `B_L_${documentId?._id}.xml`
    : isAirDoc
      ? `AWB_${documentId?._id}.xml`
      : isInternational
        ? `CMR_${documentId?._id}.xml`
        : `Delivery_Note_${documentId?._id}.xml`;

  // The primary document PDF, named per transport mode. SEA → House B/L,
  // AIR → Air Waybill, ROAD → CMR / Delivery Note.
  const pdfDescriptor = isSeaDoc
    ? { id: "bol-pdf", name: `B_L_${documentId?._id}.pdf` }
    : isAirDoc
      ? { id: "awb-pdf", name: `AWB_${documentId?._id}.pdf` }
      : isInternational
        ? { id: "cmr-pdf", name: `CMR_${documentId?._id}.pdf` }
        : { id: "delivery-note", name: `Delivery_Note_${documentId?._id}.pdf` };

  // Initialize default files function
  const getInitialFiles = useCallback((): AttachedFile[] => {
    // ROAD documents (CMR / Delivery Note) are only attachable when the
    // shipment has enough data to produce one (same Yup check as the form's
    // "Validate" button). SEA (B/L) and AIR (AWB) always render.
    const documentReady = isShipmentDocumentReady(documentId);
    const showPdf = isRoad ? documentReady : true;

    const files: AttachedFile[] = [];

    if (showPdf) {
      files.push({
        id: pdfDescriptor.id,
        name: pdfDescriptor.name,
        type: "cmr-pdf",
        size: 0,
        canDelete: false,
      });
    }

    files.push({
      id: "xml-data",
      name: xmlFileName,
      type: "xml",
      size: 0,
      canDelete: false,
    });

    return files;
  }, [documentId, isRoad, xmlFileName, pdfDescriptor.id, pdfDescriptor.name]);

  const [attachedFiles, setAttachedFiles] =
    useState<AttachedFile[]>(getInitialFiles());

  // Fetch real XML file when modal opens
  const fetchXMLFile = useCallback(async () => {
    if (!documentId) return;

    setIsXmlLoading(true);
    try {
      const result = await dispatch(
        fetchShipmentXMLFile(documentId._id),
      ).unwrap();

      // Re-wrap the returned blob so the attachment filename matches the
      // transport mode (B/L / AWB / CMR / Delivery Note) rather than the
      // generic name the slice assigns.
      const renamedFile =
        result.file instanceof Blob
          ? new File([result.file], xmlFileName, { type: "application/xml" })
          : result.file;

      // Update the XML file in attachedFiles with real data
      setAttachedFiles((prev) =>
        prev.map((file) => {
          if (file.type === "xml") {
            return {
              ...file,
              name: xmlFileName,
              size: result.size,
              file: renamedFile,
              url: undefined,
            };
          }
          return file;
        }),
      );
    } catch (error: any) {
      setErrorMessage(t("sharing.failedLoadXml"));
    } finally {
      setIsXmlLoading(false);
    }
  }, [documentId, dispatch, t, xmlFileName]);

  const fetchShipmentPDF = useCallback(async () => {
    if (!documentId) return;
    // ROAD docs require the data to pass the document-ready check; SEA (B/L)
    // and AIR (AWB) always render.
    if (isRoad && !isShipmentDocumentReady(documentId)) return;

    try {
      let pdfBytes: Uint8Array;

      if (isSeaDoc) {
        pdfBytes = await createBillOfLadingPdf(documentId, countries);
      } else if (isAirDoc) {
        pdfBytes = await createAirWaybillPdf(documentId, countries);
      } else if (isInternational) {
        pdfBytes = await createCmrPdf(documentId, countries);
      } else {
        pdfBytes = await createDeliveryNotePdf(documentId);
      }

      const safeBytes = new Uint8Array(pdfBytes);
      const blob = new Blob([safeBytes], { type: "application/pdf" });

      const fileName = pdfDescriptor.name;

      const file = new File([blob], fileName, {
        type: "application/pdf",
      });

      setAttachedFiles((prev) =>
        prev.map((f) =>
          f.id === pdfDescriptor.id
            ? { ...f, name: fileName, size: file.size, file }
            : f,
        ),
      );
    } catch (error) {
      setErrorMessage(t("sharing.failedGeneratePdf"));
    }
  }, [
    documentId,
    countries,
    isInternational,
    isRoad,
    isSeaDoc,
    isAirDoc,
    pdfDescriptor.id,
    pdfDescriptor.name,
    t,
  ]);

  // Generate the Shipment Order PDF and keep it ready as an attachment. The SO
  // is available for every shipment regardless of transport mode / readiness.
  const fetchSoPdf = useCallback(async () => {
    if (!documentId) return;
    setSoGenerating(true);
    try {
      const pdfBytes = await createShipmentOrderPdf(documentId, countries);
      const blob = new Blob([new Uint8Array(pdfBytes)], {
        type: "application/pdf",
      });
      const name = `SO_${documentId.shipmentNumber || documentId._id}.pdf`;
      setSoFile(new File([blob], name, { type: "application/pdf" }));
    } catch {
      setSoFile(null);
      setErrorMessage(t("sharing.failedGeneratePdf"));
    } finally {
      setSoGenerating(false);
    }
  }, [documentId, countries, t]);

  // Reset all states to initial values
  const resetAllStates = useCallback(() => {
    setActiveTab("email");
    setEmailData({
      recipients: "",
      subject: t("sharing.subjectDefault", { id: documentId?._id }),
      message: "",
      includePDF: true,
    });
    setSuccessMessage("");
    setErrorMessage("");
    setIsDriverLinkCopied(false);
    setIsXmlLoading(false);
    setAttachedFiles(getInitialFiles());
    setOtpEmail("");
    setSoEmail({
      recipients: "",
      subject: documentId
        ? `Shipment Order — ${documentId.shipmentNumber ?? documentId._id}`
        : "",
      message: "",
    });
    setSoFile(null);
    setEmailTags(documentId?.allowedEmails ?? []);
    setEmailTagInput("");
    setEmailTagRole("view");
    setAccessRequests([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [documentId, getInitialFiles, t]);

  // Initialize states when modal opens, reset when closes
  useEffect(() => {
    if (isOpen) {
      resetAllStates();
      fetchXMLFile();
      fetchShipmentPDF();
      fetchSoPdf();
    }
  }, [isOpen, resetAllStates, fetchXMLFile, fetchShipmentPDF, fetchSoPdf]);

  // Auto-clear success and error messages
  useEffect(() => {
    let successTimer: ReturnType<typeof setTimeout>;
    let errorTimer: ReturnType<typeof setTimeout>;

    if (successMessage) {
      successTimer = setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    }

    if (errorMessage) {
      errorTimer = setTimeout(() => {
        setErrorMessage("");
      }, 5000);
    }

    return () => {
      if (successTimer) clearTimeout(successTimer);
      if (errorTimer) clearTimeout(errorTimer);
    };
  }, [successMessage, errorMessage]);

  // Load access requests when shipment tab becomes active
  useEffect(() => {
    if (activeTab !== "shipment" || !documentId) return;
    const load = async () => {
      setLoadingRequests(true);
      try {
        const res = await shipmentAccessAPI.getAccessRequests(documentId._id);
        setAccessRequests(res.data.data.requests ?? []);
        setEmailTags(res.data.data.allowedEmails ?? []);
      } catch {
        // silently ignore
      } finally {
        setLoadingRequests(false);
      }
    };
    load();
  }, [activeTab, documentId]);

  const handleRespondToRequest = async (
    requestId: string,
    status: "accepted" | "rejected",
  ) => {
    if (!documentId) return;
    setRespondingTo(requestId);
    try {
      await shipmentAccessAPI.respondToAccessRequest(
        documentId._id,
        requestId,
        status,
      );
      setAccessRequests((prev) =>
        prev.map((r) => (r._id === requestId ? { ...r, status } : r)),
      );
      if (status === "accepted") {
        const req = accessRequests.find((r) => r._id === requestId);
        if (req && !emailTags.some((t) => t.email === req.email)) {
          setEmailTags((prev) => [...prev, { email: req.email, role: "view" }]);
        }
        toast.success(`Access granted to ${accessRequests.find((r) => r._id === requestId)?.email}`);
      } else {
        toast.success("Request rejected");
      }
    } catch {
      toast.error("Failed to update request");
    } finally {
      setRespondingTo(null);
    }
  };

  // Generate driver view URL
  const driverViewUrl = `${
    window.location.origin
  }/shipment-drivers-view?company=${encodeURIComponent(
    documentId?.delivery?.companyName || "",
  )}&token=${documentId?.driverAccess.publicLinkToken}`;

  const shipmentViewUrl = `${
    window.location.origin
  }/shipment-clients-view?shipmentId=${encodeURIComponent(
    documentId?._id || "",
  )}`;

  // Check if valid file
  const isFile = (obj: any): obj is File => {
    return (
      obj &&
      typeof obj === "object" &&
      typeof obj.name === "string" &&
      typeof obj.size === "number" &&
      typeof obj.type === "string" &&
      typeof obj.lastModified === "number" &&
      typeof obj.arrayBuffer === "function"
    );
  };

  // Robust clipboard copy function with fallback
  const copyToClipboard = async (text: string): Promise<boolean> => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {}
    }
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (successful) {
        return true;
      }
    } catch (err) {}
    try {
      const input = document.createElement("input");
      input.value = text;
      input.style.position = "absolute";
      input.style.left = "-9999px";
      document.body.appendChild(input);
      input.setSelectionRange(0, 99999);
      input.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(input);
      return successful;
    } catch (err) {
      return false;
    }
  };

  const validateEmails = (emails: string): boolean => {
    if (!emails || !emails.trim()) return false;
    const emailList = emails
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return (
      emailList.length > 0 && emailList.every((email) => emailRegex.test(email))
    );
  };

  const handleSendEmail = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!emailData.recipients.trim()) {
      toast.error(t("sharing.recipientRequired"));
      return;
    }

    if (!validateEmails(emailData.recipients)) {
      toast.error(t("sharing.invalidEmails"));
      return;
    }

    try {
      const formData = new FormData();
      formData.append("recipients", emailData.recipients.trim());
      formData.append("subject", emailData.subject.trim());
      formData.append("message", emailData.message.trim());

      let filesAdded = 0;

      for (const item of attachedFiles) {
        try {
          if (item.file) {
            if (isFile(item.file)) {
              formData.append("files", item.file);
              filesAdded++;
            }
          }
        } catch (fileError) {}
      }

      await dispatch(sendCmrAttachments(formData)).unwrap();

      toast.success(t("sharing.emailSentSuccess"));
      setEmailData({
        recipients: "",
        subject: t("sharing.subjectDefault", { id: documentId?._id }),
        message: "",
        includePDF: true,
      });
      setAttachedFiles([...getInitialFiles()]);
      await fetchXMLFile();
      await fetchShipmentPDF();
    } catch (error: any) {
      const errorMsg = error?.message || t("sharing.emailSentFailed");
      toast.error(errorMsg);
    }
  };

  const handleSendSO = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!soEmail.recipients.trim()) {
      toast.error(t("sharing.recipientRequired"));
      return;
    }
    if (!validateEmails(soEmail.recipients)) {
      toast.error(t("sharing.invalidEmails"));
      return;
    }
    if (!soFile) {
      toast.error("The Shipment Order document is still being generated.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("recipients", soEmail.recipients.trim());
      formData.append("subject", (soEmail.subject || "Shipment Order").trim());
      formData.append("message", soEmail.message.trim());
      formData.append("files", soFile);

      await dispatch(sendCmrAttachments(formData)).unwrap();

      toast.success(t("sharing.emailSentSuccess"));
      setSoEmail((prev) => ({ ...prev, recipients: "", message: "" }));
    } catch (error: any) {
      toast.error(error?.message || t("sharing.emailSentFailed"));
    }
  };

  const handleCopyDriverLink = async () => {
    try {
      const success = await copyToClipboard(driverViewUrl);
      if (success) {
        setIsDriverLinkCopied(true);
        setTimeout(() => setIsDriverLinkCopied(false), 2000);
        setSuccessMessage(t("sharing.driverLinkCopied"));
      } else {
        setErrorMessage(t("sharing.copyFailed"));
      }
    } catch (error) {
      setErrorMessage(t("sharing.copyError"));
    }
  };

  const handleCopyShipmentViewLink = async () => {
    try {
      const success = await copyToClipboard(shipmentViewUrl);
      if (success) {
        setSuccessMessage(t("sharing.shipmentLinkCopied"));
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setErrorMessage(t("sharing.copyFailed"));
      }
    } catch (error) {
      setErrorMessage(t("sharing.copyError"));
    }
  };

  const validateWhatsAppPhoneNumber = (phone: string): boolean =>
    isValidPhoneFormat(phone);

  const handleGenerateWhatsAppLink = () => {
    if (!whatsappPhoneNumber.trim()) {
      toast.error(t("sharing.whatsappNoPhone"));
      return;
    }

    if (!validateWhatsAppPhoneNumber(whatsappPhoneNumber)) {
      toast.error(t("sharing.whatsappInvalidPhone"));
      return;
    }

    const cleanPhoneNumber = whatsappPhoneNumber.replace(/\D/g, "");

    const message = t("sharing.whatsappMessage", {
      shipmentNumber: documentId?.shipmentNumber,
      link: driverViewUrl,
    });

    const whatsappUrl = `https://wa.me/${cleanPhoneNumber}?text=${encodeURIComponent(
      message,
    )}`;

    window.open(whatsappUrl, "_blank");

    toast.success(t("sharing.whatsappSuccess"));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) return;

    const newFiles: AttachedFile[] = [];

    files.forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        setErrorMessage(t("sharing.fileTooLarge", { name: file.name }));
        return;
      }

      const allowedTypes = [
        ".pdf",
        ".doc",
        ".docx",
        ".jpg",
        ".jpeg",
        ".png",
        ".txt",
      ];
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        setErrorMessage(
          t("sharing.fileTypeNotAllowed", { ext: fileExtension }),
        );
        return;
      }

      const newFile: AttachedFile = {
        id: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        type: "upload",
        size: file.size,
        file: file,
        canDelete: true,
      };
      newFiles.push(newFile);
    });

    if (newFiles.length > 0) {
      setAttachedFiles((prev) => [...prev, ...newFiles]);
      setSuccessMessage(
        t("sharing.filesAddedSuccess", { count: newFiles.length }),
      );
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (fileId: string) => {
    const file = attachedFiles.find((f) => f.id === fileId);

    if (file && file.canDelete === false) {
      setErrorMessage(t("sharing.fileCannotBeRemoved"));
      return;
    }

    setAttachedFiles((prev) => prev.filter((f) => f.id !== fileId));
    setSuccessMessage(t("sharing.fileRemovedSuccess"));
  };

  const handleOpenFile = async (file: AttachedFile) => {
    try {
      if (file.type === "xml") {
        const xmlFile = attachedFiles.find((f) => f.type === "xml")?.file;
        if (xmlFile instanceof Blob) {
          const fileURL = URL.createObjectURL(xmlFile);
          window.open(fileURL, "_blank");
        }
        setSuccessMessage(t("sharing.xmlOpenSuccess"));
      } else if (file.type === "cmr-pdf") {
        const pdfFile = attachedFiles.find((f) => f.type === "cmr-pdf")?.file;
        if (pdfFile instanceof Blob) {
          const fileURL = URL.createObjectURL(pdfFile);
          window.open(fileURL, "_blank");
        }
      } else if (file.file) {
        const url = URL.createObjectURL(file.file);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (error: any) {
      const errorMsg = error?.message || t("sharing.failedOpenFile");
      setErrorMessage(errorMsg);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return t("sharing.fileLoadingSize");
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const getFileIcon = (file: AttachedFile) => {
    switch (file.type) {
      case "cmr-pdf":
        return <FileText className="h-4 w-4 text-red-600" />;
      case "xml":
        return <FileIcon className="h-4 w-4 text-blue-600" />;
      default:
        return <FileIcon className="h-4 w-4 text-gray-600" />;
    }
  };

  const handleClose = () => {
    setErrorMessage("");
    setSuccessMessage("");
    setIsDriverLinkCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-300 bg-gray-50 shrink-0">
          <h2 className="text-2xl font-bold text-gray-900">
            {t("sharing.title")}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            icon={<X className="h-5 w-5" />}
            className="p-2!"
          />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-300 bg-gray-50 shrink-0 overflow-x-auto">
          <button
            onClick={() => setActiveTab("email")}
            className={`flex items-center space-x-2 px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === "email"
                ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            <Mail className="h-4 w-4" />
            <span>{t("sharing.tabEmail")}</span>
          </button>

          <button
            onClick={() => setActiveTab("so")}
            className={`flex items-center space-x-2 px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === "so"
                ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            <FileText className="h-4 w-4" />
            <span>SO Document</span>
          </button>

          {/* <button
            onClick={() => setActiveTab("otp")}
            className={`flex items-center space-x-2 px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === "otp"
                ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            <ShieldCheck className="h-4 w-4" />
            <span>Delivery OTP</span>
            {documentId?.deliveryOtp?.required && (
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                  documentId.deliveryOtp.used
                    ? "bg-green-100 text-green-700"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                {documentId.deliveryOtp.used ? "Used" : "Sent"}
              </span>
            )}
          </button> */}

          {isRoad && (
            <button
              onClick={() => setActiveTab("driver")}
              className={`flex items-center space-x-2 px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                activeTab === "driver"
                  ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              <Link className="h-4 w-4" />
              <span>{t("sharing.tabDriversApp")}</span>
            </button>
          )}

          <button
            onClick={() => setActiveTab("shipment")}
            className={`flex items-center space-x-2 px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === "shipment"
                ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            <Link className="h-4 w-4" />
            <span>{t("sharing.tabShipmentLink")}</span>
            {accessRequests.filter((r) => r.status === "pending").length > 0 && (
              <span className="ml-1 bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                {accessRequests.filter((r) => r.status === "pending").length}
              </span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Success/Error Messages */}
          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
              <Check className="h-5 w-5 text-green-600" />
              <span className="text-green-800">{successMessage}</span>
            </div>
          )}
          {errorMessage && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <span className="text-red-800">{errorMessage}</span>
            </div>
          )}
          {isXmlLoading && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
              <span className="text-blue-800">{t("sharing.loadingXml")}</span>
            </div>
          )}

          {/* ── Email Tab ── */}
          {activeTab === "email" && (
            <div className="space-y-6">
              <div>
                <input
                  type="text"
                  value={emailData.recipients}
                  onChange={(e) =>
                    setEmailData((prev) => ({
                      ...prev,
                      recipients: e.target.value,
                    }))
                  }
                  placeholder={t("sharing.recipientsPlaceholder")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                />
                <p className="text-sm text-gray-500 mt-1">
                  {t("sharing.recipientsHint")}
                </p>
              </div>

              <div>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) =>
                    setEmailData((prev) => ({
                      ...prev,
                      subject: e.target.value,
                    }))
                  }
                  placeholder={t("sharing.subjectPlaceholder")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                />
              </div>

              <div>
                <textarea
                  value={emailData.message}
                  onChange={(e) =>
                    setEmailData((prev) => ({
                      ...prev,
                      message: e.target.value,
                    }))
                  }
                  rows={4}
                  placeholder={t("sharing.messagePlaceholder")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                />
              </div>

              {/* Files Section */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("sharing.filesTitle")}
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    icon={<Upload className="h-4 w-4" />}
                  >
                    {t("sharing.addFiles")}
                  </Button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.csv,.xlsx"
                />

                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  {attachedFiles.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {attachedFiles.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            {getFileIcon(file)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(file.size)}
                                {file.type === "cmr-pdf" &&
                                  t("sharing.autoAttachedRequired")}
                                {file.type === "xml" &&
                                  t("sharing.autoAttached")}
                                {isXmlLoading &&
                                  file.type === "xml" &&
                                  t("sharing.loading")}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenFile(file)}
                              icon={<Eye className="h-4 w-4" />}
                              className="p-1! text-gray-500 hover:text-blue-600"
                              title={t("sharing.openFile")}
                              disabled={isXmlLoading && file.type === "xml"}
                            />
                            {file.canDelete !== false ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveFile(file.id)}
                                icon={<Trash2 className="h-4 w-4" />}
                                className="p-1! text-gray-500 hover:text-red-600"
                                title={t("sharing.removeFile")}
                              />
                            ) : (
                              <div
                                className="p-1 text-gray-300"
                                title={t("sharing.requiredFileCannotRemove")}
                              >
                                <Trash2 className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-gray-500">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">{t("sharing.noFilesAttached")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── SO Document Tab ── */}
          {activeTab === "so" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Share Shipment Order
                </h3>
                <p className="text-gray-600 text-sm">
                  Email the Shipment Order (SO) document. It is generated from
                  this shipment and attached to the email automatically.
                </p>
              </div>

              <div>
                <input
                  type="text"
                  value={soEmail.recipients}
                  onChange={(e) =>
                    setSoEmail((prev) => ({
                      ...prev,
                      recipients: e.target.value,
                    }))
                  }
                  placeholder={t("sharing.recipientsPlaceholder")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                />
                <p className="text-sm text-gray-500 mt-1">
                  {t("sharing.recipientsHint")}
                </p>
              </div>

              <div>
                <input
                  type="text"
                  value={soEmail.subject}
                  onChange={(e) =>
                    setSoEmail((prev) => ({ ...prev, subject: e.target.value }))
                  }
                  placeholder={t("sharing.subjectPlaceholder")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                />
              </div>

              <div>
                <textarea
                  value={soEmail.message}
                  onChange={(e) =>
                    setSoEmail((prev) => ({ ...prev, message: e.target.value }))
                  }
                  rows={4}
                  placeholder={t("sharing.messagePlaceholder")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                />
              </div>

              {/* Auto-attached SO document */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("sharing.filesTitle")}
                </h3>
                <div className="border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <FileText className="h-4 w-4 text-red-600" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {soFile?.name ??
                            `SO_${documentId?.shipmentNumber || documentId?._id}.pdf`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {soGenerating
                            ? t("sharing.loading")
                            : soFile
                              ? `${formatFileSize(soFile.size)}${t("sharing.autoAttachedRequired")}`
                              : t("sharing.failedGeneratePdf")}
                        </p>
                      </div>
                    </div>
                    {soFile && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const url = URL.createObjectURL(soFile);
                          window.open(url, "_blank");
                          setTimeout(() => URL.revokeObjectURL(url), 1000);
                        }}
                        icon={<Eye className="h-4 w-4" />}
                        className="p-1! text-gray-500 hover:text-blue-600"
                        title={t("sharing.openFile")}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── OTP Tab ── */}
          {activeTab === "otp" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Delivery OTP
                </h3>
                <p className="text-gray-600 text-sm">
                  Send a one-time code to the receiver. The driver must enter
                  this code to complete the journey. The OTP can only be sent
                  once and is valid until used.
                </p>
              </div>

              {documentId?.deliveryOtp?.required ? (
                /* ── Already sent state ── */
                <div className="rounded-lg border border-green-200 bg-green-50 p-5 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Check className="h-5 w-5 text-green-600 shrink-0" />
                    <span className="font-medium text-green-900">
                      OTP sent successfully
                    </span>
                  </div>
                  <div className="text-sm text-green-800 space-y-1">
                    <p>
                      Sent on{" "}
                      <span className="font-medium">
                        {new Date(
                          documentId.deliveryOtp.sentAt!,
                        ).toLocaleString()}
                      </span>
                    </p>
                  </div>
                  {documentId.deliveryOtp.used ? (
                    <div className="flex items-center space-x-2 pt-1 border-t border-green-200">
                      <Check className="h-4 w-4 text-green-600 shrink-0" />
                      <p className="text-sm text-green-800 font-medium">
                        Used by driver at{" "}
                        {new Date(
                          documentId.deliveryOtp.usedAt!,
                        ).toLocaleString()}
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 pt-1 border-t border-green-200">
                      <div className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />
                      <p className="text-sm text-amber-700">
                        Awaiting driver input — OTP not yet used
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* ── Send OTP form ── */
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Receiver email address
                    </label>
                    <input
                      type="email"
                      value={otpEmail}
                      onChange={(e) => setOtpEmail(e.target.value)}
                      placeholder="receiver@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      The receiver will get a 6-digit code to share with the
                      driver on arrival.
                    </p>
                  </div>

                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <h4 className="font-medium text-amber-900 mb-2">
                      Important
                    </h4>
                    <ul className="text-sm text-amber-800 space-y-1">
                      <li>• The OTP can only be sent once per shipment.</li>
                      <li>
                        • It is valid until the driver uses it — it does not
                        expire by time.
                      </li>
                      <li>
                        • The driver will not be able to complete the journey
                        without it.
                      </li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Driver Tab ── */}
          {activeTab === "driver" && (
            <div className="space-y-6">
              {documentId && documentId.assignedDriver ? (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t("sharing.privateDriverTitle")}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {t("sharing.privateDriverDesc")}
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t("sharing.publicDriverLinkTitle")}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {t("sharing.publicDriverLinkDesc")}
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={driverViewUrl}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-600 text-sm select-all"
                      />
                      <Button
                        variant={isDriverLinkCopied ? "primary" : "outline"}
                        size="sm"
                        onClick={handleCopyDriverLink}
                        icon={
                          isDriverLinkCopied ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Link className="h-4 w-4" />
                          )
                        }
                      >
                        {isDriverLinkCopied
                          ? t("sharing.copied")
                          : t("sharing.copyLink")}
                      </Button>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-medium text-yellow-900 mb-2">
                      {t("sharing.importantNotes")}
                    </h4>
                    <ul className="text-sm text-yellow-800 space-y-1">
                      <li>• {t("sharing.noteReadOnly")}</li>
                      <li>• {t("sharing.noteCannotModify")}</li>
                      <li>• {t("sharing.noteGps")}</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <MessageSquare className="h-5 w-5 text-green-600" />
                      <h4 className="font-medium text-green-900">
                        {t("sharing.whatsappTitle")}
                      </h4>
                    </div>
                    <p className="text-sm text-green-800 mb-4">
                      {t("sharing.whatsappDesc")}
                    </p>
                    <div className="space-y-3">
                      <div>
                        <input
                          type="tel"
                          value={whatsappPhoneNumber}
                          onChange={(e) =>
                            setWhatsappPhoneNumber(e.target.value)
                          }
                          placeholder={t("sharing.whatsappPhonePlaceholder")}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent placeholder-gray-500"
                        />
                        <p className="text-xs text-green-700 mt-1">
                          {t("sharing.whatsappPhoneHint")}
                        </p>
                      </div>
                      <button
                        onClick={handleGenerateWhatsAppLink}
                        className="w-full inline-flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <MessageSquare className="h-4 w-4" />
                        <span>{t("sharing.whatsappGenerate")}</span>
                      </button>
                    </div>
                    <div className="mt-3 text-xs text-green-700">
                      <p>
                        <strong>{t("sharing.whatsappNextSteps")}</strong>
                      </p>
                      <ul className="mt-1 space-y-1">
                        <li>• {t("sharing.whatsappStep1")}</li>
                        <li>• {t("sharing.whatsappStep2")}</li>
                        <li>• {t("sharing.whatsappStep3")}</li>
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Shipment Link Tab ── */}
          {activeTab === "shipment" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-dark-900 mb-2">
                  {t("sharing.shipmentLinkTitle")}
                </h3>
                <p className="text-dark-600 mb-4">
                  {t("sharing.shipmentLinkDesc")}
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={shipmentViewUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-dark-600 text-sm"
                  />
                  <button
                    onClick={handleCopyShipmentViewLink}
                    className="inline-flex items-center space-x-2 px-3 py-2 border border-gray-300 hover:bg-gray-50 text-dark-700 rounded-lg transition-colors"
                  >
                    <Link className="h-4 w-4" />
                    <span>{t("sharing.copyLink")}</span>
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">
                  {t("sharing.whatsSharedTitle")}
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• {t("sharing.sharedItem1")}</li>
                  <li>• {t("sharing.sharedItem2")}</li>
                  <li>• {t("sharing.sharedItem3")}</li>
                  <li>• {t("sharing.sharedItem4")}</li>
                  <li>• {t("sharing.sharedItem5")}</li>
                  <li>• {t("sharing.sharedItem6")}</li>
                </ul>
              </div>

              {/* ── Access Requests ── */}
              {(loadingRequests || accessRequests.length > 0) && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-sm text-gray-800">
                        Access Requests
                      </span>
                      {accessRequests.filter((r) => r.status === "pending")
                        .length > 0 && (
                        <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                          {
                            accessRequests.filter((r) => r.status === "pending")
                              .length
                          }{" "}
                          pending
                        </span>
                      )}
                    </div>
                  </div>

                  {loadingRequests ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {accessRequests.map((req) => (
                        <div
                          key={req._id}
                          className="flex items-center justify-between px-4 py-3"
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {req.email}
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(req.createdAt).toLocaleDateString(
                                undefined,
                                { day: "numeric", month: "short", year: "numeric" },
                              )}
                            </p>
                          </div>

                          <div className="flex items-center space-x-2">
                            {req.status === "pending" && (
                              <>
                                <button
                                  onClick={() =>
                                    handleRespondToRequest(req._id, "accepted")
                                  }
                                  disabled={respondingTo === req._id}
                                  className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                                >
                                  {respondingTo === req._id ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                  ) : (
                                    "Accept"
                                  )}
                                </button>
                                <button
                                  onClick={() =>
                                    handleRespondToRequest(req._id, "rejected")
                                  }
                                  disabled={respondingTo === req._id}
                                  className="inline-flex items-center px-3 py-1 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {req.status === "accepted" && (
                              <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                Accepted
                              </span>
                            )}
                            {req.status === "rejected" && (
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-full">
                                  Rejected
                                </span>
                                <button
                                  onClick={() =>
                                    handleRespondToRequest(req._id, "accepted")
                                  }
                                  disabled={respondingTo === req._id}
                                  className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                >
                                  {respondingTo === req._id ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                  ) : (
                                    "Grant Access"
                                  )}
                                </button>
                              </div>
                            )}
                            {req.status === "revoked" && (
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded-full border border-orange-200">
                                  Revoked
                                </span>
                                <button
                                  onClick={() =>
                                    handleRespondToRequest(req._id, "accepted")
                                  }
                                  disabled={respondingTo === req._id}
                                  className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                >
                                  {respondingTo === req._id ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                  ) : (
                                    "Grant Access"
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Email Access Control ── */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center space-x-2">
                    <Lock className="h-4 w-4 text-amber-600" />
                    <span className="font-medium text-sm text-gray-800">
                      {emailTags.length > 0
                        ? `Private — ${emailTags.length} email${emailTags.length > 1 ? "s" : ""} allowed`
                        : "Private — add emails to grant access"}
                    </span>
                  </div>
                  {savingEmails && (
                    <span className="text-xs text-gray-400 animate-pulse">Saving…</span>
                  )}
                </div>

                <div className="p-4 space-y-3">
                  <p className="text-sm text-gray-600">
                    Add email addresses that are allowed to access this shipment.
                    Each email can be assigned a role: <strong>Standard</strong> (can view, file reclamations, etc.) or{" "}
                    <strong>POH Handler</strong> (same as Standard, plus can sign the proof of delivery).
                  </p>

                  {/* Tag list */}
                  {emailTags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {emailTags.map((entry) => (
                        <span
                          key={entry.email}
                          className={`inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full border ${
                            entry.role === "poh_handler"
                              ? "bg-purple-50 text-purple-800 border-purple-200"
                              : "bg-blue-50 text-blue-800 border-blue-200"
                          }`}
                        >
                          <span>{entry.email}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                            entry.role === "poh_handler"
                              ? "bg-purple-200 text-purple-900"
                              : "bg-blue-200 text-blue-900"
                          }`}>
                            {entry.role === "poh_handler" ? "POH" : "Standard"}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const next = emailTags.map((e) =>
                                e.email === entry.email
                                  ? { ...e, role: (e.role === "view" ? "poh_handler" : "view") as "view" | "poh_handler" }
                                  : e,
                              );
                              setEmailTags(next);
                              autoSaveEmails(next);
                            }}
                            className="hover:text-amber-600 transition-colors"
                            title="Toggle role"
                          >
                            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const next = emailTags.filter((e) => e.email !== entry.email);
                              setEmailTags(next);
                              autoSaveEmails(next);
                            }}
                            className="hover:text-red-600 transition-colors"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Input row */}
                  <div className="flex items-center gap-2">
                    <input
                      type="email"
                      value={emailTagInput}
                      onChange={(e) => setEmailTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                          e.preventDefault();
                          const val = emailTagInput.trim().toLowerCase();
                          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          if (!emailRegex.test(val)) { toast.error("Enter a valid email address"); return; }
                          if (emailTags.some((t) => t.email === val)) { toast.error("Email already added"); return; }
                          const next = [...emailTags, { email: val, role: emailTagRole }];
                          setEmailTags(next);
                          setEmailTagInput("");
                          autoSaveEmails(next);
                        }
                      }}
                      placeholder="email@example.com — press Enter to add"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                    />
                    <select
                      value={emailTagRole}
                      onChange={(e) => setEmailTagRole(e.target.value as "view" | "poh_handler")}
                      className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700"
                    >
                      <option value="view">Standard</option>
                      <option value="poh_handler">POH Handler</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        const val = emailTagInput.trim().toLowerCase();
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(val)) { toast.error("Enter a valid email address"); return; }
                        if (emailTags.some((t) => t.email === val)) { toast.error("Email already added"); return; }
                        const next = [...emailTags, { email: val, role: emailTagRole }];
                        setEmailTags(next);
                        setEmailTagInput("");
                        autoSaveEmails(next);
                      }}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
                      title="Add email"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center space-x-3 p-6 border-t border-gray-300 bg-gray-50 shrink-0">
          <Button variant="outline" onClick={handleClose}>
            {t("sharing.close")}
          </Button>

          {activeTab === "email" && (
            <Button
              variant="primary"
              onClick={handleSendEmail}
              isLoading={loading}
              icon={<Send className="h-4 w-4" />}
            >
              {loading ? t("sharing.sending") : t("sharing.sendEmail")}
            </Button>
          )}

          {activeTab === "so" && (
            <Button
              variant="primary"
              onClick={handleSendSO}
              isLoading={loading}
              disabled={soGenerating || !soFile}
              icon={<Send className="h-4 w-4" />}
            >
              {loading ? t("sharing.sending") : t("sharing.sendEmail")}
            </Button>
          )}


          {activeTab === "otp" && !documentId?.deliveryOtp?.required && (
            <Button
              variant="primary"
              onClick={async () => {
                if (!otpEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(otpEmail)) {
                  toast.error("Please enter a valid email address");
                  return;
                }
                try {
                  await dispatch(
                    sendDeliveryOtp({
                      shipmentId: documentId!._id,
                      recipientEmail: otpEmail,
                    }),
                  ).unwrap();
                  toast.success(`OTP sent to ${otpEmail}`);
                } catch (err: any) {
                  toast.error(err?.message || "Failed to send OTP");
                }
              }}
              isLoading={otpLoading}
              icon={<Send className="h-4 w-4" />}
            >
              {otpLoading ? "Sending…" : "Send OTP"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CMRSharingModal;
